// 總覽健康儀表（HEALTH-01）的純函式：憑證到期、錄影缺口、席次、AI 額度、備份覆蓋、韌體合規。

/** 憑證到期：已過期 / 30 天內到期。 */
export const CERT_WARN_DAYS = 30;

export function certState(notAfter: string | undefined, now: number): 'expired' | 'soon' | 'ok' | 'unknown' {
  if (!notAfter) return 'unknown';
  const t = Date.parse(notAfter);
  if (!Number.isFinite(t)) return 'unknown';
  if (t <= now) return 'expired';
  return t - now <= CERT_WARN_DAYS * 86400_000 ? 'soon' : 'ok';
}

/** 一格錄到的秒數：平台回 `covered_seconds`（cloud/platform internal/vms Bucket）；`seconds` 只為相容舊回應。 */
export function coveredSeconds(b: { covered_seconds?: number; seconds?: number }): number {
  return b.covered_seconds ?? b.seconds ?? 0;
}

/**
 * 全時錄影的攝影機過去 24 小時有沒有缺口：時間軸桶（resolution_seconds 一格）裡錄到的秒數不到九成就算缺。
 * 只看全時席次（事件錄影本來就只有事件時段）；最後一格可能還在寫，不算。
 * 讀 `covered_seconds`（之前讀不存在的 `seconds`，每一格都算缺錄；M2-02 發現）。
 */
export function timelineGaps(buckets: { start: string; covered_seconds?: number; seconds?: number }[], resolutionS: number): number {
  let gaps = 0;
  for (const b of buckets.slice(0, -1)) if (coveredSeconds(b) < resolutionS * 0.9) gaps++;
  return gaps;
}

/** 席次使用：已指派 / 全部（生效中的）。 */
export function seatUsage(seats: { status: string; assigned_ref?: string }[]): { used: number; total: number } {
  const active = seats.filter((s) => s.status === 'active' || s.status === 'grace');
  return { used: active.filter((s) => !!s.assigned_ref).length, total: active.length };
}

/** 備份政策健康彙總：degraded / partial / unknown 分開算，ok 與 not_recording 算正常。 */
export function backupSummary(policies: { status?: string; health?: string }[]): { ok: number; degraded: number; other: number; total: number } {
  const out = { ok: 0, degraded: 0, other: 0, total: 0 };
  for (const p of policies) {
    if (p.status !== 'active' && p.status !== 'degraded') continue;
    out.total++;
    const h = p.health ?? 'unknown';
    if (h === 'ok' || h === 'not_recording') out.ok++;
    else if (h === 'degraded' || p.status === 'degraded') out.degraded++;
    else out.other++;
  }
  return out;
}

/** 第一個缺口那格的開始（秒）；沒有 → undefined（最後一格不算，同 timelineGaps）。 */
export function firstGapStart(buckets: { start: string; covered_seconds?: number; seconds?: number }[], resolutionS: number): number | undefined {
  for (const b of buckets.slice(0, -1))
    if (coveredSeconds(b) < resolutionS * 0.9) {
      const ms = Date.parse(b.start);
      return Number.isFinite(ms) ? Math.floor(ms / 1000) : undefined;
    }
  return undefined;
}

/**
 * 本地覆蓋回報落後超過這麼久就當「沒回報」（錄影主機離線或上報卡住；SPEC #vms-web-827「錄影缺口」）：10 分鐘，跟離線通知
 * status_offline_minutes 預設一致（Eddy 2026-10-18 選單，app/vms#19；原本 1 小時）。正常每 30–60 秒一筆。
 */
export const LOCAL_STALE_MS = 10 * 60_000;

/**
 * façade 時間軸的本地覆蓋（SPEC #vms-40「本地覆蓋」）：source_status.local 沒有 / unknown → 沒有上報；known_until.local 落後 10 分鐘以上 → 沒回報；
 * 否則回 gaps.local（只算到 known_until）。
 */
export function localTimelineState(tl: Record<string, unknown> | null | undefined, now: number): { reporting: false } | { reporting: true; gaps: { from: number; to: number }[] } {
  const st = (tl?.source_status as Record<string, { status?: string }> | undefined)?.local;
  if (!st || !st.status || st.status === 'unknown') return { reporting: false };
  const known = Date.parse(String((tl?.known_until as Record<string, unknown> | undefined)?.local ?? ''));
  if (!Number.isFinite(known) || now - known > LOCAL_STALE_MS) return { reporting: false };
  const raw = ((tl?.gaps as Record<string, unknown> | undefined)?.local ?? []) as { from?: string; to?: string }[];
  const gaps = (Array.isArray(raw) ? raw : [])
    .map((g) => ({ from: Date.parse(g.from ?? ''), to: Math.min(Date.parse(g.to ?? ''), known) }))
    .filter((g) => Number.isFinite(g.from) && Number.isFinite(g.to) && g.to > g.from);
  return { reporting: true, gaps };
}

/** 本地缺口（該錄卻沒錄）合計 ≥ 這麼多秒才算缺。 */
export const LOCAL_GAP_MIN_S = 60;

/** 缺口原因（SPEC #storage-rec-health）：帶了就是該錄卻沒錄。recorder_restart = nvrd 開機補的重開機缺口（device/nvr#221）。 */
export const LOCAL_GAP_REASONS = ['source_unreachable', 'storage_full', 'storage_error', 'recorder_restart'] as const;
/** 顯示用的原因：上面四個，或 unknown（沒帶原因的全天錄影缺口、不認得的值） */
export type LocalGapReason = (typeof LOCAL_GAP_REASONS)[number] | 'unknown';

/**
 * recorder-coverage 的缺口（SPEC #vms-web-827「錄影缺口」）：偵測 / 排程錄影的通道只算帶原因的（沒帶原因的是照錄影方式本來就沒錄）；
 * 全天錄影的通道（continuous）沒帶原因的也算、標 unknown（「原因不明」）。回合計秒數（重疊合併）、最早一段的開始（秒）、
 * 合計秒數最多的原因（「需要處理」那一行寫在攝影機名後面）。
 */
export function localGaps(
  rows: { gaps?: { from: string; to: string; reason?: string }[] }[],
  opts: { continuous?: boolean } = {},
): { sec: number; first?: number; reason?: LocalGapReason } {
  const spans = rows
    .flatMap((r) => r.gaps ?? [])
    .filter((g) => !!g.reason || opts.continuous)
    .map((g) => ({ a: Date.parse(g.from), b: Date.parse(g.to), reason: (LOCAL_GAP_REASONS as readonly string[]).includes(g.reason ?? '') ? (g.reason as LocalGapReason) : ('unknown' as const) }))
    .filter((g) => Number.isFinite(g.a) && Number.isFinite(g.b) && g.b > g.a)
    .sort((x, y) => x.a - y.a);
  let sec = 0;
  let end = -Infinity;
  const by = new Map<LocalGapReason, number>();
  for (const g of spans) {
    const s = Math.max(g.a, end);
    if (g.b > s) sec += (g.b - s) / 1000;
    end = Math.max(end, g.b);
    by.set(g.reason, (by.get(g.reason) ?? 0) + (g.b - g.a));
  }
  let reason: LocalGapReason | undefined;
  for (const [r, ms] of by) if (reason === undefined || ms > (by.get(reason) ?? 0)) reason = r;
  return { sec: Math.round(sec), first: spans.length ? Math.floor(spans[0].a / 1000) : undefined, reason };
}

/** 帶原因的本地缺口（偵測 / 排程錄影的通道的算法）；同 localGaps 不帶 continuous。 */
export function reasonedGaps(rows: { gaps?: { from: string; to: string; reason?: string }[] }[]): { sec: number; first?: number } {
  const { sec, first } = localGaps(rows);
  return { sec, first };
}

/**
 * 這一路是不是全天錄影（SPEC #vms-web-827「全天錄影的判斷」）：façade recording-policy 的 device_mode.reported = continuous
 * （一般排程整週全開，或雲端全時帶出的 mode_source = cloud_policy）；其他值、讀不到 → false（照偵測 / 排程錄影算，不猜）。
 */
export function isContinuousRecording(policy: unknown): boolean {
  const dm = (policy as { device_mode?: { reported?: string | null } | null } | null | undefined)?.device_mode;
  return dm?.reported === 'continuous';
}
